import { useMemo } from 'react'
import { AtendimentoType, CargoType, ContratoType, Permission } from '../../../helpers'
import { UsuarioWithImage } from '../../../../app/modules/auth/core/_models'
import { acessoEscala } from '../../../../app/modules/apps/escalas/core/escalaHelpers'
import { isVinculoMedico, temVinculoMedico } from '../../../../app/modules/auth/core/vinculos'

/**
 * Quais itens do menu lateral o usuário logado vê. Função pura (testável sem renderizar) com o
 * mesmo critério que o menu já usava; ao restringir uma tela, ajuste aqui e no
 * `allowedPermissions` da rota em `routesConfig.tsx`.
 */
export const calcularMenuPermissoes = (currentUser?: UsuarioWithImage) => {
  const vinculos = currentUser?.vinculos?.filter((vinculo) => vinculo.ativo)
  const grupo = currentUser?.grupo?.nome

  const superAdmin = grupo === Permission.SuperAdmin
  const medicoAdmin = grupo === Permission.SuperMedico
  const gerente = grupo === Permission.SuperGerente
  const admin = grupo === Permission.Admin
  const employee = grupo === Permission.Employee
  const paciente = grupo === Permission.Usuario
  const solicitante = grupo === Permission.Solicitante
  const isMedico = temVinculoMedico(vinculos)
  const colaborador = vinculos?.some((v) => v.cargo === CargoType.Colaborador) ?? false
  const responsavel =
    vinculos?.some(
      (v) =>
        v.cargo === CargoType.Responsavel || v.cargo === CargoType.Administrador || v.responsavel
    ) ?? false
  const hasASO =
    vinculos?.some((v) => v?.empresa?.tiposAtendimento?.includes(AtendimentoType.Ocupacional)) ??
    false
  const hasIC =
    vinculos?.some(
      (v) =>
        isVinculoMedico(v) && v?.empresa?.tiposAtendimento?.includes(AtendimentoType.Interconsulta)
    ) ?? false
  const hasClientesContratos =
    vinculos?.some((v) => v.responsavel && v?.empresa?.permiteCadastroCliente) ?? false
  const hasAgenda =
    vinculos?.some(
      (v) =>
        (v.responsavel || v.gestorAgenda) &&
        v.empresa?.tiposContrato?.some((contrato) => contrato === ContratoType.Corpo_Medico)
    ) ?? false

  const isFinanceiro = superAdmin || gerente
  // Escala de atendimento: mesmo critério das abas da página (`acessoEscala`), para o
  // item não levar a "sem acesso" nem sumir para quem tem uma aba.
  const acessoEscalas = acessoEscala(currentUser)

  return {
    paciente,
    solicitante,
    isMedico,
    isFinanceiro,
    // Tudo o que é exclusivo de SUPER_ADMIN (seção Super Admin)
    superAdmin,
    empresa: superAdmin || gerente,
    clienteContratos: superAdmin || ((gerente || medicoAdmin || admin) && hasClientesContratos),
    api: superAdmin,
    especialidades: superAdmin || medicoAdmin,
    empresaCliente:
      superAdmin || ((responsavel || colaborador || medicoAdmin || employee) && hasASO),
    funcionarios: superAdmin || responsavel || medicoAdmin || gerente,
    pacientes: superAdmin || responsavel || colaborador || medicoAdmin || isMedico,
    assinarAso: (superAdmin || isMedico || medicoAdmin) && hasASO,
    downloadAso: superAdmin || ((responsavel || colaborador || medicoAdmin || employee) && hasASO),
    relatorios: superAdmin || medicoAdmin || gerente || responsavel,
    avaliacoesPendentes: superAdmin || medicoAdmin || isMedico,
    avaliacoes: superAdmin || isMedico || medicoAdmin || colaborador || responsavel,
    formularios: superAdmin || admin,
    protocolosTriagem: superAdmin || admin || medicoAdmin || isMedico,
    meusAtendimentos: superAdmin || medicoAdmin || gerente || isMedico,
    importar: superAdmin || responsavel || colaborador || medicoAdmin,
    relatorioNps: superAdmin || medicoAdmin || gerente || responsavel || admin,
    indicadores: superAdmin || medicoAdmin || admin,
    unidades: superAdmin || admin,
    // Mesmos grupos de `Esus:read`/`Esus:resend` e do `allowedPermissions` da rota `apps/esus/*`.
    esusEnvios: superAdmin || admin,
    pareceres: superAdmin || hasIC,
    monitoramento: superAdmin || admin || medicoAdmin,
    agenda: superAdmin || hasAgenda,
    escalas: acessoEscalas.eixoPessoal || acessoEscalas.eixoEmpresa,
    portalTransparencia: superAdmin || gerente || admin || responsavel || isMedico,
    atendimentosNaoFaturados: isFinanceiro,
    faturas: isFinanceiro,
    faturasResponsavel: gerente || admin,
    faturasProfissional: isMedico,
    notas: isFinanceiro || isMedico,
    atendimentosGestao: superAdmin,
    // Estoque SNCR é da pessoa com vínculo médico; o grupo sozinho (ex.: SUPER_ADMIN sem CRM) não tem o que ver.
    sncr: isMedico,
  }
}

export type MenuPermissoes = ReturnType<typeof calcularMenuPermissoes>

export const useMenuPermissoes = (currentUser?: UsuarioWithImage): MenuPermissoes =>
  useMemo(() => calcularMenuPermissoes(currentUser), [currentUser])
